import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios'; 
import styles from './ProductDetailPage.module.scss';
import productService from '../../services/productService';
import ProductCard from '../../components/common/ProductCard/ProductCard';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import { 
  IoStar, IoRibbonOutline, IoCarOutline, 
  IoShieldCheckmarkOutline, IoReturnUpBackOutline, IoExpandOutline, 
  IoChevronBack, IoChevronForward, IoCameraOutline, IoCloseCircle, IoImageOutline, IoChatbubblesOutline
} from 'react-icons/io5';

import { useLoading } from '../../context/LoadingContext/LoadingContext';
// [SEO] Import Helmet
import { Helmet } from 'react-helmet-async';

// --- LIGHTBOX IMPORTS ---
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails"; 
import "yet-another-react-lightbox/plugins/counter.css"; 
import "yet-another-react-lightbox/plugins/thumbnails.css"; 

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const BACKEND_URL = 'https://huongtramcomoc.vn'; 
const API_REVIEW_URL = 'https://huongtramcomoc.vn/api/endpoints/';
const ZALO_LINK = 'https://zalo.me/0987654321'; 

// [FIX URL] Hàm xử lý URL ảnh an toàn: Mã hóa ký tự đặc biệt và khoảng trắng
const getFullImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  
  // Đảm bảo có dấu / ở đầu path
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Ghép URL
  const fullUrl = `${BACKEND_URL}${cleanPath}`;
  
  // Mã hóa URL để xử lý tiếng Việt và khoảng trắng (quan trọng cho Zalo/FB)
  return encodeURI(fullUrl);
};

// Hàm cắt chữ gọn gàng cho mô tả SEO
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substr(0, text.lastIndexOf(' ', maxLength)) + '...';
};

const DetailSkeleton = () => (
  <div className={styles.skeletonWrapper}>
    <div className={`${styles.skeleton} ${styles.skeletonImg}`}></div>
    <div className={styles.skeletonInfo}>
       <div className={`${styles.skeleton} ${styles.skTitle}`}></div>
       <div className={`${styles.skeleton} ${styles.skLine}`} style={{width: '60%'}}></div>
       <div className={`${styles.skeleton} ${styles.skPrice}`}></div>
       <div className={`${styles.skeleton} ${styles.skBtn}`}></div>
    </div>
  </div>
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [attributes, setAttributes] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  
  const [openLightbox, setOpenLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [reviews, setReviews] = useState([]);
  const [newRating, setNewRating] = useState(5);
  const [reviewerName, setReviewerName] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  
  const [reviewImages, setReviewImages] = useState([]); 
  const [reviewImagePreviews, setReviewImagePreviews] = useState([]); 
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [allReviewImages, setAllReviewImages] = useState([]); 
  const [openReviewLightbox, setOpenReviewLightbox] = useState(false);
  const [reviewLightboxIndex, setReviewLightboxIndex] = useState(0);

  const [isLoadingLocal, setIsLoadingLocal] = useState(true);
  const { showLoading, hideLoading } = useLoading();

  const prevRefRelated = useRef(null);
  const nextRefRelated = useRef(null);
  const prevRefRecent = useRef(null);
  const nextRefRecent = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset Prerender khi vào trang mới
    window.prerenderReady = false; 
    fetchPageData();
    return () => {
        reviewImagePreviews.forEach(url => URL.revokeObjectURL(url));
        stopCamera();
    };
  }, [id]);

  // [PRERENDER FIX] Logic báo hiệu chụp ảnh SEO
  useEffect(() => {
    if (!isLoadingLocal) {
      // Delay 500ms để đảm bảo React Helmet đã cập nhật thẻ Meta xong
      const timer = setTimeout(() => {
        window.prerenderReady = true;
      }, 500); 
      return () => clearTimeout(timer);
    }
  }, [isLoadingLocal]);

  useEffect(() => {
    if (reviews.length > 0) {
        let collectedImages = [];
        reviews.forEach(rev => {
            let imgs = [];
            try {
                if (Array.isArray(rev.images)) imgs = rev.images;
                else if (typeof rev.images === 'string') imgs = JSON.parse(rev.images);
            } catch (e) { imgs = []; }
            
            if (Array.isArray(imgs) && imgs.length > 0) {
                // Dùng getFullImageUrl để fix lỗi link ảnh review
                const formattedImgs = imgs.map(img => ({ src: getFullImageUrl(img) }));
                collectedImages = [...collectedImages, ...formattedImgs];
            }
        });
        setAllReviewImages(collectedImages);
    }
  }, [reviews]);

  const fetchPageData = async () => {
    try {
      setIsLoadingLocal(true);
      const productRes = await productService.getProductById(id);
      const productData = productRes.data;
      setProduct(productData);

      const images = productData.images || [];
      const thumbnail = images.find(img => img.is_thumbnail == 1);
      
      // Dùng getFullImageUrl để fix lỗi link ảnh sản phẩm
      if (thumbnail) setSelectedImageUrl(getFullImageUrl(thumbnail.file_path));
      else if (images.length > 0) setSelectedImageUrl(getFullImageUrl(images[0].file_path));

      if (productData.attributes) {
        try { setAttributes(JSON.parse(productData.attributes)); } catch (e) { setAttributes([]); }
      }

      let thumbUrlForStorage = productData.thumbnail_url;
      if (!thumbUrlForStorage && images.length > 0) thumbUrlForStorage = images[0].file_path;
      const productToStore = { ...productData, thumbnail_url: thumbUrlForStorage };
      addToRecentlyViewed(productToStore);

      await Promise.all([
        productData.category_id ? productService.getAllProducts(productData.category_id).then(r => {
          setRelatedProducts(r.data.records.filter(p => p.id !== productData.id));
        }) : Promise.resolve(),
        axios.get(`${API_REVIEW_URL}/get_reviews.php?product_id=${id}`).then(res => setReviews(res.data))
      ]);

      loadRecentlyViewed();
    } catch (error) {
      console.error("Lỗi tải trang:", error);
      toast.error("Lỗi tải dữ liệu.");
    } finally {
      setIsLoadingLocal(false);
    }
  };

  const addToRecentlyViewed = (currentProduct) => {
    try {
        let recent = JSON.parse(localStorage.getItem('recently_viewed')) || [];
        recent = recent.filter(p => String(p.id) !== String(currentProduct.id));
        recent.unshift({
          id: currentProduct.id,
          name: currentProduct.name,
          price: currentProduct.price,
          sale_price: currentProduct.sale_price,
          thumbnail_url: currentProduct.thumbnail_url,
          category_name: currentProduct.category_name,
          sales_count: currentProduct.sales_count
        });
        if (recent.length > 10) recent.pop();
        localStorage.setItem('recently_viewed', JSON.stringify(recent));
      } catch (e) { console.error(e); }
  };

  const loadRecentlyViewed = () => {
    try {
      const recent = JSON.parse(localStorage.getItem('recently_viewed')) || [];
      setRecentlyViewed(recent.filter(p => String(p.id) !== String(id)));
    } catch (e) { setRecentlyViewed([]); }
  };

  const handleAddImageClick = () => {
      if (reviewImages.length >= 3) { toast.warning("Tối đa 3 ảnh."); return; }
      setShowSourceModal(true);
  };

  const handleSelectFromLibrary = () => {
      if (fileInputRef.current) fileInputRef.current.click();
      setTimeout(() => setShowSourceModal(false), 100);
  };

  const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      const remainingSlots = 3 - reviewImages.length;
      if (remainingSlots <= 0) { toast.warning("Bạn chỉ được đăng tối đa 3 ảnh."); return; }
      const allowedFiles = files.slice(0, remainingSlots);
      const newPreviews = allowedFiles.map(file => URL.createObjectURL(file));
      setReviewImages(prev => [...prev, ...allowedFiles]);
      setReviewImagePreviews(prev => [...prev, ...newPreviews]);
      e.target.value = null; 
  };

  const handleOpenCamera = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          toast.error("Trình duyệt không hỗ trợ camera."); return;
      }
      setShowSourceModal(false); setShowCameraModal(true);
      try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
          streamRef.current = stream;
          if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(e => console.error(e)); }
      } catch (err) { toast.error("Không thể mở camera."); setShowCameraModal(false); }
  };

  const handleCapturePhoto = () => {
      if (videoRef.current && canvasRef.current) {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          canvas.width = video.videoWidth; canvas.height = video.videoHeight;
          const context = canvas.getContext('2d');
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
              const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
              const previewUrl = URL.createObjectURL(file);
              if (reviewImages.length < 3) {
                  setReviewImages(prev => [...prev, file]);
                  setReviewImagePreviews(prev => [...prev, previewUrl]);
                  toast.success("Đã chụp ảnh!");
              }
              stopCamera(); setShowCameraModal(false);
          }, 'image/jpeg', 0.8);
      }
  };

  const stopCamera = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(track => track.stop()); streamRef.current = null; } };
  const handleCloseCamera = () => { stopCamera(); setShowCameraModal(false); };
  const handleRemoveImage = (index) => {
    URL.revokeObjectURL(reviewImagePreviews[index]);
    setReviewImages(prev => prev.filter((_, i) => i !== index));
    setReviewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReview = (e) => {
    e.preventDefault();
    if (!reviewerName.trim()) return toast.error("Vui lòng nhập tên!");
    showLoading(); 
    const formData = new FormData();
    formData.append('product_id', id);
    formData.append('user_name', reviewerName);
    formData.append('rating', newRating);
    formData.append('comment', reviewComment);
    reviewImages.forEach((file) => formData.append('review_images[]', file));

    axios.post(`${API_REVIEW_URL}/add_review.php`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then(() => {
      toast.success("Đánh giá thành công!");
      setReviewerName(''); setReviewComment(''); setNewRating(5);
      setReviewImages([]); setReviewImagePreviews([]);
      return axios.get(`${API_REVIEW_URL}/get_reviews.php?product_id=${id}`); 
    }).then(res => setReviews(res.data))
    .catch(() => toast.error("Lỗi gửi đánh giá"))
    .finally(() => hideLoading());
  };

  const handleOpenReviewLightbox = (imgSrc) => {
      const fullSrc = getFullImageUrl(imgSrc);
      const index = allReviewImages.findIndex(img => img.src === fullSrc);
      setReviewLightboxIndex(index !== -1 ? index : 0);
      setOpenReviewLightbox(true);
  };

  const averageRating = reviews.length > 0 ? (reviews.reduce((acc, r) => acc + parseInt(r.rating), 0) / reviews.length).toFixed(1) : 5;
  const originalPrice = product ? Number(product.price) : 0;
  const salePrice = product ? Number(product.sale_price || 0) : 0;
  const hasDiscount = salePrice > 0 && salePrice < originalPrice;
  const currentPrice = hasDiscount ? salePrice : originalPrice;
  const discountPercent = hasDiscount ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : 0;
  const isContactPrice = currentPrice === 0;

  const createSwiperSettings = (prevRef, nextRef) => ({
    modules: [Navigation, Pagination, Autoplay],
    spaceBetween: 20, loop: false,
    navigation: { prevEl: prevRef.current, nextEl: nextRef.current },
    onBeforeInit: (swiper) => { if (swiper.params.navigation) { swiper.params.navigation.prevEl = prevRef.current; swiper.params.navigation.nextEl = nextRef.current; } },
    autoplay: { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true },
    breakpoints: { 0: { slidesPerView: 2.2, spaceBetween: 10 }, 640: { slidesPerView: 2.2, spaceBetween: 15 }, 1024: { slidesPerView: 4, spaceBetween: 20 } }
  });

  const thumbSwiperSettings = {
    modules: [Navigation], spaceBetween: 10, slidesPerView: 4, navigation: true,
    breakpoints: { 0: { slidesPerView: 2.2 }, 480: { slidesPerView: 3 }, 1024: { slidesPerView: 3.5 } }
  };

  if (isLoadingLocal) return <div className={styles.pageWrapper}><div className={styles.container}><DetailSkeleton/></div></div>;
  if (!product) return null; 

  // --- [SEO START - TỐI ƯU CHO ẢNH VUÔNG] ---
  const productSlides = product?.images?.map(img => ({ src: getFullImageUrl(img.file_path) })) || [];
  let seoImage = getFullImageUrl('/seo-banner.jpg'); 
  
  if (product.thumbnail_url) {
      seoImage = getFullImageUrl(product.thumbnail_url);
  } else if (productSlides.length > 0) {
      seoImage = productSlides[0].src;
  }

  const seoTitle = `${product.name} - Tinh Hoa Trầm Việt | Hương Trầm Cổ Mộc`;
  let seoDescriptionRaw = product.short_description || `Trải nghiệm ${product.name} từ 100% trầm hương tự nhiên. Mang lại không gian an yên, đẳng cấp và thu hút vượng khí.`;
  const seoDescription = truncateText(seoDescriptionRaw, 160);

  const pageUrl = window.location.href;

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": productSlides.map(img => img.src),
    "description": seoDescription,
    "sku": `${product.id}`,
    "brand": { "@type": "Brand", "name": "Hương Trầm Cổ Mộc" },
    "offers": {
      "@type": "Offer",
      "url": pageUrl,
      "priceCurrency": "VND",
      "price": currentPrice,
      "availability": product.stock_quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition"
    }
  };

  if (reviews.length > 0) {
    schemaData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": reviews.length
    };
    schemaData.review = reviews.slice(0, 5).map(rev => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": rev.user_name },
      "datePublished": rev.created_at,
      "reviewBody": rev.comment,
      "reviewRating": { "@type": "Rating", "ratingValue": rev.rating }
    }));
  }
  // --- [SEO END] ---

  return (
    <div className={styles.pageWrapper}>
      {/* --- HELMET SEO --- */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={pageUrl} />

        {/* Facebook / Zalo Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="800" />
        
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Hương Trầm Cổ Mộc" />
        
        <meta property="product:price:amount" content={currentPrice} />
        <meta property="product:price:currency" content="VND" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />

        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>
      
      {showSourceModal && (
          <div className={styles.modalOverlay} onClick={() => setShowSourceModal(false)}>
              <div className={styles.sourceModal} onClick={e => e.stopPropagation()}>
                  <h3>Thêm ảnh đánh giá</h3>
                  <div className={styles.sourceOptions}>
                      <button onClick={handleSelectFromLibrary} className={styles.sourceBtn}><IoImageOutline /> Tải từ thư viện</button>
                      <button onClick={handleOpenCamera} className={styles.sourceBtn}><IoCameraOutline /> Chụp ảnh trực tiếp</button>
                  </div>
                  <button className={styles.cancelBtn} onClick={() => setShowSourceModal(false)}>Hủy</button>
              </div>
          </div>
      )}

      {showCameraModal && (
          <div className={styles.modalOverlay}>
              <div className={styles.cameraModal}>
                  <div className={styles.cameraHeader}><h3>Chụp ảnh</h3><button onClick={handleCloseCamera}><IoCloseCircle /></button></div>
                  <div className={styles.cameraView}><video ref={videoRef} autoPlay playsInline muted></video><canvas ref={canvasRef} style={{ display: 'none' }}></canvas></div>
                  <div className={styles.cameraFooter}><button onClick={handleCapturePhoto} className={styles.captureBtn}><div className={styles.captureInner}></div></button></div>
              </div>
          </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileChange} />

      <div className={styles.breadcrumb}><div className={styles.container}><Link to="/">Trang chủ</Link> / <Link to="/san-pham">Sản phẩm</Link> / <span>{product.name}</span></div></div>

      <div className={styles.container}>
        <div className={styles.cardWrapper}>
            <div className={styles.productMain}>
              <div className={styles.galleryCol}>
                <div className={styles.mainImageFrame} onClick={() => setOpenLightbox(true)}>
                  <img src={selectedImageUrl} alt={product.name} />
                  {hasDiscount && <span className={styles.badgeDiscount}>-{discountPercent}%</span>}
                  <div className={styles.zoomIcon}><IoExpandOutline /></div>
                </div>
                {product.images?.length > 1 && (
                  <div className={styles.thumbSlider}>
                    <Swiper {...thumbSwiperSettings} className={styles.thumbSwiper}>
                        {product.images.map((img, idx) => (
                            <SwiperSlide key={idx}>
                                <div className={`${styles.thumbItem} ${selectedImageUrl.includes(img.file_path) ? styles.activeThumb : ''}`} onClick={() => setSelectedImageUrl(getFullImageUrl(img.file_path))}>
                                    <img src={getFullImageUrl(img.file_path)} alt="thumb" />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                  </div>
                )}
              </div>

              <div className={styles.infoCol}>
                <h1 className={styles.title}>{product.name}</h1>
                <div className={styles.meta}>
                  <div className={styles.rating}>{[...Array(5)].map((_, i) => <IoStar key={i} className={i < Math.round(averageRating) ? styles.starFill : styles.starEmpty} />)} <span>({reviews.length} đánh giá)</span></div>
                  <span className={styles.divider}>|</span> <span className={styles.sold}>Đã bán {product.sales_count || 0}</span>
                </div>
                
                <div className={styles.priceWrap}>
                  {isContactPrice ? (
                    <span className={styles.contactPriceText}>Liên hệ</span>
                  ) : (
                    <>
                      <span className={styles.finalPrice}>{new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(currentPrice)}</span>
                      {hasDiscount && <span className={styles.oldPrice}>{new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(originalPrice)}</span>}
                    </>
                  )}
                </div>

                <p className={styles.shortDesc}>{product.short_description}</p>
                
                <div className={styles.actionRow}>
                  {!isContactPrice && (
                    <div className={styles.qtyBox}>
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                        <input value={quantity} readOnly />
                        <button onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}>+</button>
                    </div>
                  )}
                  
                  {isContactPrice ? (
                    <a href={ZALO_LINK} target="_blank" rel="noopener noreferrer" className={`${styles.btnAddCart} ${styles.btnContact}`}>
                      <IoChatbubblesOutline /> Liên Hệ Ngay
                    </a>
                  ) : (
                    <button className={styles.btnAddCart} onClick={() => {addToCart(product, quantity); toast.success("Đã thêm vào giỏ!")}} disabled={product.stock_quantity <= 0}>
                      {product.stock_quantity > 0 ? "Thêm Vào Giỏ Hàng" : "Hết Hàng"}
                    </button>
                  )}
                </div>

                <div className={styles.policyGrid}>
                  <div className={styles.policyItem}><IoRibbonOutline /> <span>Hàng chính hãng</span></div>
                  <div className={styles.policyItem}><IoCarOutline /> <span>Freeship từ 2 triệu</span></div>
                  <div className={styles.policyItem}><IoShieldCheckmarkOutline /> <span>Bảo hành trọn đời</span></div>
                  <div className={styles.policyItem}><IoReturnUpBackOutline /> <span>Đổi trả 7 ngày</span></div>
                </div>
              </div>
            </div>
        </div>

        <div className={styles.cardWrapper}>
            <div className={styles.contentTabs}>
                <div className={styles.tabBlock}>
                    <h3 className={styles.tabTitle}>Thông Tin Chi Tiết</h3>
                    <div className={styles.specTable}>
                        {attributes.map((attr, idx) => (
                            <div key={idx} className={styles.specRow}>
                                <span className={styles.specKey}>{attr.key}:</span>
                                <span className={styles.specVal}>{attr.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.dividerLine}></div>
                <div className={styles.tabBlock}>
                    <h3 className={styles.tabTitle}>Mô Tả Sản Phẩm</h3>
                    <div className={styles.htmlDesc} dangerouslySetInnerHTML={{ __html: product.description }} />
                </div>
            </div>
        </div>

        <div className={styles.cardWrapper}>
            <div className={styles.tabBlock}>
              <h3 className={styles.tabTitle}>Đánh Giá Từ Khách Hàng ({reviews.length})</h3>
              <div className={styles.reviewBox}>
                  <div className={styles.reviewForm}>
                    <h4>Viết đánh giá của bạn</h4>
                    <div className={styles.starsInput}>{[1,2,3,4,5].map(s => (<IoStar key={s} className={s <= newRating ? styles.sFill : styles.sEmpty} onClick={() => setNewRating(s)}/>))}</div>
                    <input placeholder="Tên của bạn" value={reviewerName} onChange={e => setReviewerName(e.target.value)} />
                    <textarea placeholder="Chia sẻ trải nghiệm của bạn..." value={reviewComment} onChange={e => setReviewComment(e.target.value)} />
                    <div className={styles.imageUploadSection}>
                        <div className={styles.previewContainer}>
                            {reviewImagePreviews.map((src, index) => (
                                <div key={index} className={styles.previewItem}>
                                    <img src={src} alt="Preview" />
                                    <button onClick={() => handleRemoveImage(index)} className={styles.removeBtn}><IoCloseCircle /></button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={handleAddImageClick} className={styles.uploadBtn}><IoCameraOutline /> Thêm ảnh / Chụp ảnh</button>
                    </div>
                    <button onClick={handleSubmitReview} className={styles.submitBtn}>Gửi Đánh Giá</button>
                  </div>
                  
                  <div className={styles.reviewList}>
                    {reviews.length === 0 && <p className={styles.noReviewText}>Chưa có đánh giá nào cho sản phẩm này.</p>}
                    {reviews.map(rev => {
                      let displayImages = [];
                      try { if (Array.isArray(rev.images)) displayImages = rev.images; else if (typeof rev.images === 'string') displayImages = JSON.parse(rev.images); } catch (e) { displayImages = []; }
                      return (
                        <div key={rev.id} className={styles.reviewItem}>
                            <div className={styles.avatar}>{rev.user_name.charAt(0)}</div>
                            <div className={styles.revContentWrapper}>
                              <div className={styles.rName}>{rev.user_name}</div>
                              <div className={styles.rStars}>{[...Array(5)].map((_,i) => <IoStar key={i} className={i < rev.rating ? styles.starFill : styles.starEmpty}/>)}</div>
                              <p className={styles.rText}>{rev.comment}</p>
                              {displayImages && displayImages.length > 0 && (
                                  <div className={styles.revImagesGrid}>
                                      {displayImages.map((imgSrc, idx) => (<img key={idx} src={getFullImageUrl(imgSrc)} alt="Review" className={styles.revImg} onClick={() => handleOpenReviewLightbox(imgSrc)} />))}
                                  </div>
                              )}
                              <span className={styles.rDate}>{new Date(rev.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                      );
                    })}
                  </div>
              </div>
            </div>
        </div>

        <div className={styles.bottomSection}>
            {relatedProducts.length > 0 && (
              <div className={styles.relatedSec}>
                 <div className={styles.secHeader}><h2 className={styles.secTitle}>Sản Phẩm Tương Tự</h2></div>
                 <div className={styles.swiperContainerRel}>
                    <div ref={prevRefRelated} className={styles.customPrevBtn}><IoChevronBack /></div>
                    <div ref={nextRefRelated} className={styles.customNextBtn}><IoChevronForward /></div>
                    <div className={styles.swiperWrap}>
                        <Swiper {...createSwiperSettings(prevRefRelated, nextRefRelated)} className={styles.prodSwiper}>
                        {relatedProducts.map(p => (<SwiperSlide key={p.id} className={styles.slideItem}><ProductCard product={p} /></SwiperSlide>))}
                        </Swiper>
                    </div>
                 </div>
              </div>
            )}
            
            {recentlyViewed.length > 0 && (
              <div className={styles.relatedSec}>
                 <div className={styles.secHeader}><h2 className={styles.secTitle}>Sản Phẩm Vừa Xem</h2></div>
                 <div className={styles.swiperContainerRel}>
                    <div ref={prevRefRecent} className={styles.customPrevBtn}><IoChevronBack /></div>
                    <div ref={nextRefRecent} className={styles.customNextBtn}><IoChevronForward /></div>
                    <div className={styles.swiperWrap}>
                        <Swiper {...createSwiperSettings(prevRefRecent, nextRefRecent)} className={styles.prodSwiper}>
                        {recentlyViewed.map(p => (<SwiperSlide key={p.id} className={styles.slideItem}><ProductCard product={p} /></SwiperSlide>))}
                        </Swiper>
                    </div>
                 </div>
              </div>
            )}
        </div>
      </div>

      <Lightbox 
        open={openLightbox} 
        close={() => setOpenLightbox(false)} 
        index={lightboxIndex} 
        slides={productSlides} 
        plugins={[Zoom, Fullscreen, Counter, Thumbnails]} 
      />
      
      <Lightbox 
        open={openReviewLightbox} 
        close={() => setOpenReviewLightbox(false)} 
        index={reviewLightboxIndex} 
        slides={allReviewImages} 
        plugins={[Zoom, Fullscreen, Counter, Thumbnails]} 
      />
    </div>
  );
};

export default ProductDetailPage;